/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useCallback, useEffect, useState } from 'react';
import { Grid, Typography, InputAdornment, Box } from '@mui/material';
import { isEmpty } from 'modules/common/helpers/object';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, useFormikContext } from 'formik';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { selectNotification } from 'modules/common/notifications/selectors';
import {
  selectOrganizationId,
  selectAuthUser,
  selectSessionId,
  selectSchemaMapping,
} from 'modules/common/auth/selectors';
import ActionButton from 'modules/common/components/action-button';
import { dashboardActions } from 'modules/dashboard/slice';
import { notificationActions } from 'modules/common/notifications/slice';
import Modal from 'modules/common/components/modal';
import ERROR_TYPES from 'modules/common/constants/error-types';
import { Alert, TextField } from 'modules/common/components/add-hotel-group/style';
import addFilterFormValidation from 'modules/common/components/add-hotel-group/validation/add-filter-form-validation';
import {
  selectExistedHotelGroup,
  selectHotelGroupDropdownList,
  selectHotelGroupName,
  selectHotelGroupQuery,
  selectIsUserConfigurationNotExist,
  selectHotelDescription,
} from 'modules/dashboard/selectors';
import { dataHandler } from '@phg/advanced-querying-module';
import { decode } from 'html-entities';
import logger from 'modules/common/utils/logger';

/**
 * used to inject formik context in-order to control form field from outside of formik context
 * @param {String} selectedFilterName - Name of the loaded hotel group
 * @constructor
 */
const FormikContextInjector = ({ selectedFilterName, selectedDescription }) => {
  const { setFieldValue, setFieldTouched } = useFormikContext();
  useEffect(() => {
    if (selectedFilterName) {
      setFieldValue('filterName', selectedFilterName);
      setFieldTouched('filterName', false);
    } else {
      setFieldValue('filterName', '');
      setFieldTouched('filterName', false);
    }
    if (selectedDescription) {
      setFieldValue('description', selectedDescription);
      setFieldTouched('description', false);
    } else {
      setFieldValue('description', '');
      setFieldTouched('description', false);
    }
  }, [selectedFilterName, selectedDescription]);
};

/** Implementation to save hotel group
 * @param {Boolean} open - To open/ close dialog box
 * @param {Function} onClose - Function to trigger on close event
 * @returns
 */
const SaveFilterDialog = ({
  groupList,
  hotelList,
  onClose,
  setGroupList,
  setTemGroupList,
  setAllClear,
  resetForm,
  isPrimary,
  setFilterError,
  filterError,
}) => {
  const dispatch = useDispatch();
  //
  const organizationId = useSelector(selectOrganizationId);
  const notification = useSelector(selectNotification);
  const currentUser = useSelector(selectAuthUser);
  const selectedHotelName = useSelector(selectHotelGroupName);
  const selectedHotelDescription = useSelector(selectHotelDescription);
  const existedFilter = useSelector(selectExistedHotelGroup);
  const isUserConfigurationNotExist = useSelector(selectIsUserConfigurationNotExist);
  const hotelQuery = useSelector(selectHotelGroupQuery);
  const groupsList = useSelector(selectHotelGroupDropdownList);
  const sessionId = useSelector(selectSessionId);
  const hotelMappingSchema = useSelector(selectSchemaMapping);
  //
  const [openOverwriteModal, setOpenOverwriteModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [saveValueObj, setSaveValueObj] = useState(null);
  const [hotelSetNameLetterCount, setHotelSetNameLetterCount] = useState(0);
  const [hotelSeDescriptionLetterCount, setHotelSetDescriptionLetterCount] = useState(0);

  /**
   * Local structured logger helper for this component.
   * Adds common context fields and routes the log event through the generic logger.
   *
   * @param {Object} params
   * @param {string} params.name - Event name.
   * @param {string} [params.level='info'] - Log level.
   * @param {Object} [params.context={}] - Additional context specific to this log.
   */
  const filterLog = useCallback(
    ({ name, level = 'info', context = {} }) => {
      if (currentUser?.username) {
        logger.setUser({ username: currentUser.username });
      }
      logger.logEvent({
        name,
        level,
        context: {
          sessionId,
          organizationId,
          userId: currentUser?.id,
          ...context,
        },
      });
    },
    [currentUser, sessionId, organizationId]
  );
  //
  useEffect(() => {
    if (selectedHotelName) {
      setHotelSetNameLetterCount(selectedHotelName.length);
    }
    if (selectedHotelDescription) {
      setHotelSetDescriptionLetterCount(selectedHotelDescription.length);
    }
  }, [selectedHotelName, selectedHotelDescription]);
  //
  useEffect(() => {
    if (notification?.isEnabled && notification?.type === ERROR_TYPES.SUCCESS) {
      dispatch(notificationActions.resetNotification());
      setTemGroupList([]);
      setGroupList([]);
      dispatch(dashboardActions.setHotelGroupQuery(''));
      dispatch(dashboardActions.setSelectedHotelGroupName(''));
      dispatch(dashboardActions.setSelectedHotelGroupDescription(''));
      onClose();
    }
  }, [notification]);
  // create save filter object and dispatch validate filter name
  const createFilterAndValidateFilterName = async (values) => {
    dispatch(dashboardActions.resetHotelGroupNameValidationStatus());
    const isFavorite =
      groupsList?.find((hotel) => hotel?.label === values?.filterName?.trim())?.isFavorite ?? false;
    const valueObj = {
      userId: currentUser?.id,
      organizationId,
      hotelGroups: [
        {
          hotelGroupName: values?.filterName.trim(),
          description: values?.description.trim(),
          hotelQuery,
          isFavorite,
        },
      ],
    };
    //
    setSaveValueObj(valueObj);

    const query = `hotelGroupName=${values.filterName.trim()}`;
    dispatch(
      dashboardActions.getHotelGroupNameValidation({
        organizationId,
        userId: currentUser?.id,
        query,
      })
    );
  };
  //
  useEffect(() => {
    if (hotelQuery) {
      try {
        dataHandler(decode(hotelQuery), hotelList, hotelMappingSchema);
        setFilterError(false);
      } catch (e) {
        filterLog({
          name: 'hotel_group_query_parse_error',
          level: 'error',
          context: {
            hotelQuery,
            errorMessage: e?.message,
          },
        });
        setFilterError(true);
      }
    }
  }, [hotelQuery, filterLog]);

  // dispatch save hotel group , reset save object, filter validation state and close update warning modal
  const saveSubmit = (filterObject) => {
    dispatch(dashboardActions.createHotelGroup(filterObject));
    setSaveValueObj(null);
    dispatch(dashboardActions.resetHotelGroupNameValidationStatus());
    dispatch(dashboardActions.resetUserConfigurationStatus());
    setOpenOverwriteModal(false);
    setFilterError(false);
    dispatch(
      dashboardActions.setHotelSetUpdated({
        primary: {
          updated: isPrimary,
          name: isPrimary ? filterObject.hotelGroups[0].hotelGroupName : null,
        },
        secondary: {
          updated: !isPrimary,
          name: !isPrimary ? filterObject.hotelGroups[0].hotelGroupName : null,
        },
      })
    );
  };
  // delete selected hotel set
  const deleteFilters = (filterId) => {
    const query = `hotelGroupName=${filterId}`;
    dispatch(
      dashboardActions.deleteHotelGroup({
        organizationId,
        userId: currentUser?.id,
        query,
      })
    );
  };
  // checks hotel group name already exists and open update warning modal
  useEffect(() => {
    if (saveValueObj !== null && existedFilter !== null && !isUserConfigurationNotExist) {
      if (existedFilter) {
        // check whether existing hotel set is modified or not
        const isHotelSetNotModified = existedFilter?.hotelGroups?.find(
          (group) =>
            group?.hotelGroupName === saveValueObj?.hotelGroups?.[0]?.hotelGroupName &&
            group?.hotelQuery === saveValueObj?.hotelGroups?.[0]?.hotelQuery &&
            group?.description === saveValueObj?.hotelGroups?.[0]?.description
        );
        if (!isHotelSetNotModified) {
          setOpenOverwriteModal(true);
        } else {
          onClose();
        }
      } else {
        saveSubmit(saveValueObj);
      }
    }
    // if user configuration is not exist, save the hotel group
    if (isUserConfigurationNotExist && saveValueObj !== null && existedFilter === null) {
      saveSubmit(saveValueObj);
    }
  }, [saveValueObj, existedFilter, isUserConfigurationNotExist]);
  //
  useEffect(() => {
    dispatch(notificationActions.resetNotification());
  }, []);
  //
  return (
    <Formik
      initialValues={{
        filterName: selectedHotelName ?? '',
        description: selectedHotelDescription ?? '',
      }}
      validationSchema={addFilterFormValidation}
      onSubmit={(values) => createFilterAndValidateFilterName(values)}
    >
      {({ errors, handleBlur, setFieldValue, handleSubmit, values, touched, isSubmitting }) => (
        <form noValidate onSubmit={handleSubmit}>
          <Grid container direction="row" justifyContent="flex-start" alignItems="start">
            <Grid item xs={4} paddingRight={1}>
              <TextField
                name="filterName"
                value={values.filterName}
                InputLabelProps={{
                  shrink: true,
                  sx: {
                    bgcolor: 'background.paper',
                    pr: 1,
                  },
                }}
                error={Boolean(touched.filterName && errors.filterName)}
                fullWidth
                onBlur={handleBlur}
                onChange={(event) => {
                  setFieldValue('filterName', event.target.value);
                  dispatch(dashboardActions.setSelectedHotelGroupName(event.target.value));
                }}
                label="Hotel Set Name"
                size="small"
                onKeyUp={(event) => setHotelSetNameLetterCount(event.target.value.length ?? 0)}
                InputProps={{
                  inputProps: { maxLength: 20 },
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography sx={{ fontSize: 11 }}>{hotelSetNameLetterCount}/20</Typography>
                    </InputAdornment>
                  ),
                }}
              />
              <span>
                <Typography
                  sx={{ whiteSpace: 'pre-line', fontSize: 11, color: 'red', minHeight: 15 }}
                >
                  {(touched.filterName && errors.filterName) ?? ' '}
                </Typography>
              </span>
            </Grid>
            <Grid item xs={8} paddingRight={1} paddingBottom={3}>
              <Box sx={{ position: 'relative', width: '100%' }}>
                <TextField
                  name="description"
                  value={values.description}
                  InputLabelProps={{
                    shrink: true,
                    sx: {
                      bgcolor: 'background.paper',
                      pr: 1,
                    },
                  }}
                  error={Boolean(touched.description && errors.description)}
                  fullWidth
                  onBlur={handleBlur}
                  onChange={(event) => {
                    setFieldValue('description', event.target.value);
                    dispatch(dashboardActions.setSelectedHotelGroupDescription(event.target.value));
                  }}
                  label="Hotel Set Description"
                  variant="outlined"
                  size="small"
                  multiline
                  rows={2.5}
                  InputProps={{
                    inputProps: { maxLength: 200 },
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 'auto',
                      paddingBottom: '20px', // leave space for counter
                    },
                  }}
                  onKeyUp={(event) =>
                    setHotelSetDescriptionLetterCount(event.target.value.length ?? 0)
                  }
                />
                <Typography
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    right: 12,
                    fontSize: 11,
                    color: 'text.secondary',
                  }}
                >
                  {hotelSeDescriptionLetterCount}/200
                </Typography>
              </Box>

              <span>
                <Typography
                  sx={{ whiteSpace: 'pre-line', fontSize: 11, color: 'red', minHeight: 15 }}
                >
                  {(touched.description && errors.description) ?? ' '}
                </Typography>
              </span>
            </Grid>
            <Grid item container flexDirection="row" justifyContent="flex-end">
              <Grid item paddingRight={1}>
                <ActionButton
                  label="Clear All"
                  onClick={(e) => {
                    e.preventDefault();
                    setAllClear(true);
                  }}
                  disabled={!selectedHotelName && !selectedHotelDescription}
                  sx={{ maxWidth: 90 }}
                />
              </Grid>
              <Grid item paddingRight={1}>
                <ActionButton
                  label="Delete"
                  onClick={(e) => {
                    e.preventDefault();
                    setOpenDeleteModal(true);
                  }}
                  disabled={!selectedHotelName}
                  sx={{ maxWidth: 50 }}
                />
              </Grid>
              <Grid item paddingRight={1}>
                <ActionButton
                  label="Cancel"
                  onClick={() => {
                    setFilterError(false);
                    setGroupList([]);
                    setTemGroupList([]);
                    dispatch(dashboardActions.setHotelGroupQuery(''));
                    dispatch(dashboardActions.setSelectedHotelGroupName(''));
                    dispatch(dashboardActions.setSelectedHotelGroupDescription(''));
                    onClose();
                  }}
                  sx={{ maxWidth: 50 }}
                />
              </Grid>
              <Grid item>
                <ActionButton
                  label="Save"
                  variant="contained"
                  color="success"
                  onClick={() => {}}
                  disabled={isSubmitting || isEmpty(groupList) || filterError}
                />
              </Grid>
            </Grid>

            <FormikContextInjector
              selectedFilterName={selectedHotelName}
              selectedDescription={selectedHotelDescription}
            />
          </Grid>
          <Grid>
            {notification?.isEnabled && notification?.type === ERROR_TYPES.ERROR && (
              <Alert severity={notification?.type}>{notification?.message}</Alert>
            )}
          </Grid>
          <Modal
            open={openOverwriteModal}
            handleClose={() => {
              dispatch(dashboardActions.resetHotelGroupNameValidationStatus());
              setOpenOverwriteModal(false);
            }}
            title="Update Hotel Set"
            content="Hotel Set Name already exists. Are you sure you want to overwrite the hotel set?"
            handleSuccess={() => saveSubmit(saveValueObj)}
            closeLabel="Cancel"
            successLabel="Update"
            variant="contained"
            color="error"
          />
          <Modal
            open={openDeleteModal}
            handleClose={() => setOpenDeleteModal(false)}
            title="Delete Hotel Set"
            content="Are you sure you want to delete the hotel set?"
            handleSuccess={() => {
              deleteFilters(selectedHotelName);
              resetForm();
              onClose();
            }}
            closeLabel="Cancel"
            successLabel="Delete"
            variant="contained"
            color="error"
          />
        </form>
      )}
    </Formik>
  );
};
//
export default SaveFilterDialog;
